import * as React from 'react'
import {omitBy} from 'lodash'
import type {Merge} from 'type-fest'

type JustifyContentT =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'initial'
  | 'inherit'
type FlexWrapT = 'wrap' | 'nowrap' | 'wrap-reverse'
type AlignItemsT =
  'stretch' | 'center' | 'flex-start' | 'flex-end' | 'baseline' | 'initial' | 'inherit'

type FlexDirectionT = 'row' | 'column'

type FlexProps = {
  flexDirection?: FlexDirectionT
  justifyContent?: JustifyContentT
  flexWrap?: FlexWrapT
  alignItems?: AlignItemsT
  flex?: string
  gap?: string
  inline?: boolean
}

type Props = Merge<
  {
    children?: React.ReactNode
    className?: string
    dataTest?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: React.ElementType<any>
    style?: React.CSSProperties
    // Accessibility
    role?: string
    ariaLabel?: string
    id?: string
  },
  FlexProps
>

export function Flex(props: Props): React.ReactElement<Props> {
  const {
    className,
    justifyContent,
    flexDirection,
    flexWrap,
    alignItems,
    flex,
    gap,
    as: MayComponent,
    dataTest,
    role,
    ariaLabel,
    style,
    inline,
    id
  } = props
  const Component = MayComponent ?? 'div'

  const styles = {
    display: inline ? 'inline-flex' : 'flex',
    justifyContent,
    flexDirection,
    flexWrap,
    alignItems,
    flex,
    gap,
    ...style
  }

  const ariaLabelProp =
    ariaLabel !== null && ariaLabel !== undefined ? {'aria-label': ariaLabel} : {}

  return React.createElement(
    Component,
    {
      className,
      style: omitBy(styles, v => v === undefined),
      'data-test': dataTest,
      role,
      id,
      ...ariaLabelProp
    },
    [props.children]
  )
}
